import { ageTicks, findCrossing } from '../chart/fanDomain'
import { bandPath, linePath } from '../chart/geometry'
import { linearScale, niceTicks } from '../chart/scale'
import { BRAND } from '../brand'
import { formatPercent } from '../utils/formatINR'
import { DISCLAIMER, eyebrow } from './copy'
import type { CardLayout, TextBlock } from './layout'
import { FONT_DISPLAY, FONT_UI, WEIGHT_HERO, WEIGHT_LABEL } from './layout'
import type { CardModel } from './model'
import type { CardPalette } from './palette'
import { cardPalette, confidenceColor } from './palette'

/**
 * The card, drawn.
 *
 * This is the only module in the project that touches a canvas context, which is
 * deliberate: everything decidable — the colours, the copy, the geometry — is
 * settled by pure modules that `npm test` can check in node, and what is left
 * here is the part that genuinely needs a rendering surface. The recording-context
 * tests in `card.test.ts` then check the parts of *this* file that can be checked
 * without one: the draw order, the fact that no `oklch()` string ever reaches a
 * fill style, that the dash pattern is reset, and that the clip cannot leak.
 *
 * Draw order mirrors `FanChart.tsx` exactly, because the card has to look like a
 * portrait of the app rather than a second interpretation of the same data.
 */

/** Canvas 2D, narrowed to what the painter uses, so a test fake can implement it. */
export type PaintContext = CanvasRenderingContext2D

export function paintCard(ctx: PaintContext, model: CardModel, layout: CardLayout): void {
  const palette = cardPalette(model.theme)

  paintBackground(ctx, layout, palette)
  paintEyebrow(ctx, model, layout, palette)
  paintClaim(ctx, model, layout, palette)
  paintChart(ctx, model, layout, palette)
  paintDisclaimer(ctx, layout, palette)
  paintBrand(ctx, layout, palette)
}

function paintBackground(
  ctx: PaintContext,
  layout: CardLayout,
  palette: CardPalette,
): void {
  // A share card is the one component in this design system that must set its own
  // background: a PNG has no page behind it to inherit from.
  ctx.fillStyle = palette.bg
  ctx.fillRect(0, 0, layout.width, layout.height)
}

function paintEyebrow(
  ctx: PaintContext,
  model: CardModel,
  layout: CardLayout,
  palette: CardPalette,
): void {
  // Suppressed on the link preview, where the claim is the tagline and "chance of
  // FIRE by 45" would be describing a number that is not there.
  if (model.probability === null) return

  ctx.fillStyle = palette.textTertiary
  ctx.font = `${WEIGHT_LABEL} ${layout.eyebrow.size}px ${FONT_UI}`
  drawTracked(ctx, eyebrow(model.targetAge).toUpperCase(), layout.eyebrow)
}

function paintClaim(
  ctx: PaintContext,
  model: CardModel,
  layout: CardLayout,
  palette: CardPalette,
): void {
  if (model.probability === null) {
    // The link preview: the tagline, which asks a question rather than making a
    // claim, so it needs no qualification of its own.
    //
    // Only the landscape arrangement reserves room for a claim that wraps. The
    // types allow a null probability at any size, and drawing the tagline into
    // the portrait claim box — sized for "70%", one line — put three lines of
    // 184px text straight through the chart. So fit it to the box that exists
    // rather than trusting the caller to have picked a compatible size.
    ctx.fillStyle = palette.textPrimary
    const block = fitBlock(ctx, BRAND.tagline, layout.claim)
    ctx.font = `${WEIGHT_HERO} ${block.size}px ${FONT_DISPLAY}`
    drawWrapped(ctx, BRAND.tagline, block)
    return
  }

  ctx.fillStyle = confidenceColor(model.probability, model.theme)
  ctx.font = `${WEIGHT_HERO} ${layout.claim.size}px ${FONT_DISPLAY}`
  drawTracked(ctx, formatPercent(model.probability), layout.claim)
}

function paintChart(
  ctx: PaintContext,
  model: CardModel,
  layout: CardLayout,
  palette: CardPalette,
): void {
  const { plot } = layout.chart
  const ages = model.ages
  if (ages.length === 0) return

  const x = linearScale([ages[0], ages[ages.length - 1]], [plot.x, plot.x + plot.width])
  const y = linearScale([0, model.yMax], [plot.y + plot.height, plot.y])

  // Grid first, under everything.
  ctx.strokeStyle = palette.grid
  ctx.lineWidth = layout.strokes.grid
  for (const tick of niceTicks(0, model.yMax, 4)) {
    const ty = Math.round(y(tick)) + 0.5
    ctx.beginPath()
    ctx.moveTo(plot.x, ty)
    ctx.lineTo(plot.x + plot.width, ty)
    ctx.stroke()
  }

  ctx.save()
  // The app clips for a reason that applies here identically: past the target age
  // the p90 band leaves the top of the frame, because the scale is set by the
  // decision rather than by the tallest outcome.
  ctx.beginPath()
  ctx.rect(plot.x, plot.y, plot.width, plot.height)
  ctx.clip()

  fillBand(ctx, bandPath(ages, model.p10, model.p90, x, y), plot, palette.bandOuter)
  fillBand(ctx, bandPath(ages, model.p25, model.p75, x, y), plot, palette.bandInner)

  const median = new Path2D(linePath(ages, model.p50, x, y))
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  // A wide faint pass under a crisp one, exactly as the app fakes its glow —
  // cheaper than a real blur and identical in appearance.
  ctx.strokeStyle = palette.medianGlow
  ctx.lineWidth = layout.strokes.medianGlow
  ctx.stroke(median)
  ctx.strokeStyle = palette.medianLine
  ctx.lineWidth = layout.strokes.median
  ctx.stroke(median)

  // The only dashed line on the chart.
  ctx.strokeStyle = palette.requirement
  ctx.lineWidth = layout.strokes.requirement
  ctx.setLineDash([...layout.strokes.requirementDash])
  ctx.stroke(new Path2D(linePath(ages, model.required, x, y)))
  // Reset before anything else strokes, or the target rule inherits the dashes.
  ctx.setLineDash([])

  ctx.restore()

  paintTargetRule(ctx, model, layout, palette, x)
  paintCrossing(ctx, model, layout, palette, x, y)
  paintAgeAxis(ctx, model, layout, palette, x)
}

/**
 * Fills a band path with the left-to-right gradient the app declares in SVG.
 *
 * SVG's `gradientUnits` defaults to `objectBoundingBox`, so `x1=0 -> x2=1` spans
 * the *path's* bounding box. Both bands run the full age range, so their bbox is
 * exactly the plot rectangle and a gradient across the plot is an exact match.
 * That equivalence would stop holding if a band were ever drawn over a partial
 * range.
 */
function fillBand(
  ctx: PaintContext,
  d: string,
  plot: { x: number; width: number },
  stops: readonly [string, string],
): void {
  if (d === '') return
  const gradient = ctx.createLinearGradient(plot.x, 0, plot.x + plot.width, 0)
  gradient.addColorStop(0, stops[0])
  gradient.addColorStop(1, stops[1])
  ctx.fillStyle = gradient
  ctx.fill(new Path2D(d))
}

function paintTargetRule(
  ctx: PaintContext,
  model: CardModel,
  layout: CardLayout,
  palette: CardPalette,
  x: (value: number) => number,
): void {
  const { plot } = layout.chart
  const ages = model.ages
  const clamped = Math.max(ages[0], Math.min(ages[ages.length - 1], model.targetAge))
  const tx = Math.round(x(clamped)) + 0.5

  ctx.strokeStyle = palette.targetRule
  ctx.lineWidth = layout.strokes.targetRule
  ctx.beginPath()
  ctx.moveTo(tx, plot.y)
  ctx.lineTo(tx, plot.y + plot.height)
  ctx.stroke()

  ctx.fillStyle = palette.textTertiary
  ctx.font = `${WEIGHT_LABEL} ${layout.chart.axis.size}px ${FONT_DISPLAY}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'
  ctx.fillText(`age ${Math.round(model.targetAge)}`, tx, plot.y - layout.unit * 0.8)
}

function paintCrossing(
  ctx: PaintContext,
  model: CardModel,
  layout: CardLayout,
  palette: CardPalette,
  x: (value: number) => number,
  y: (value: number) => number,
): void {
  const crossing = findCrossing(model.ages, model.p50, model.required)
  if (crossing === null) return

  const cx = x(crossing.age)
  const cy = y(crossing.value)
  const r = layout.strokes.markerRadius

  // The app pulses the halo and fires a ring on the threshold crossing. A still
  // image gets the resting state — the moment, not the animation of it.
  ctx.fillStyle = palette.markerHalo
  ctx.beginPath()
  ctx.arc(cx, cy, r * 2.25, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = palette.markerDot
  ctx.strokeStyle = palette.markerRing
  ctx.lineWidth = r * 0.5
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fill()
  ctx.stroke()
}

/**
 * Ages along the bottom, and nothing up the side.
 *
 * The app's y-axis ticks are `formatINRTick` — "₹2 Cr". Those are exactly the
 * figures the card exists to leave off, so the y-axis carries no labels at all.
 * A pleasant side effect: the rupee sign sits outside both webfonts'
 * `unicode-range`, so dropping it also removes the card's only glyph that would
 * have rendered from a system fallback.
 */
function paintAgeAxis(
  ctx: PaintContext,
  model: CardModel,
  layout: CardLayout,
  palette: CardPalette,
  x: (value: number) => number,
): void {
  const { plot } = layout.chart
  ctx.fillStyle = palette.textMuted
  ctx.font = `${WEIGHT_LABEL} ${layout.chart.axis.size}px ${FONT_DISPLAY}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  for (const age of ageTicks(model.ages)) {
    ctx.fillText(String(Math.round(age)), x(age), plot.y + plot.height + layout.unit * 1.1)
  }
}

function paintDisclaimer(
  ctx: PaintContext,
  layout: CardLayout,
  palette: CardPalette,
): void {
  // Same typeface as the claim, deliberately. The CCPA Guidelines, 2022 require a
  // disclaimer to be in the same language and the same font as the claim it
  // qualifies, and the claim here is set in the display face. It costs nothing to
  // comply and it is the kind of detail a complaint would land on.
  ctx.fillStyle = palette.textMuted
  ctx.font = `${WEIGHT_LABEL} ${layout.disclaimer.size}px ${FONT_DISPLAY}`

  let y = layout.disclaimer.y
  for (const line of DISCLAIMER) {
    drawTracked(ctx, line, { ...layout.disclaimer, y })
    y += layout.disclaimer.lineHeight
  }
}

function paintBrand(ctx: PaintContext, layout: CardLayout, palette: CardPalette): void {
  ctx.fillStyle = palette.textSecondary
  ctx.font = `${WEIGHT_LABEL} ${layout.brandName.size}px ${FONT_DISPLAY}`
  drawTracked(ctx, BRAND.name, layout.brandName)

  ctx.fillStyle = palette.textMuted
  ctx.font = `${WEIGHT_LABEL} ${layout.brandUrl.size}px ${FONT_UI}`
  drawTracked(ctx, BRAND.shareUrl, layout.brandUrl)
}

/**
 * Draws text with explicit letter spacing, one glyph at a time.
 *
 * `ctx.letterSpacing` exists — and is Chrome 99+, Safari 17.4+, Firefox 126+.
 * Everywhere else, assigning it is **silently ignored**, which is the same class
 * of failure as an `oklch()` fill and lands on the same in-app-browser traffic:
 * no exception, just a card whose type is set differently from every other card.
 * The strings here are short enough that measuring per glyph costs nothing, and
 * doing the advance by hand also gives exact optical centring for free.
 */
export function drawTracked(ctx: PaintContext, text: string, block: TextBlock): void {
  const previousAlign = ctx.textAlign
  ctx.textBaseline = 'top'
  ctx.textAlign = 'left'

  const glyphs = [...text]
  const width =
    glyphs.reduce((total, glyph) => total + ctx.measureText(glyph).width, 0) +
    block.tracking * Math.max(0, glyphs.length - 1)

  let x = block.align === 'center' ? block.x - width / 2 : block.x
  for (const glyph of glyphs) {
    ctx.fillText(glyph, x, block.y)
    x += ctx.measureText(glyph).width + block.tracking
  }

  ctx.textAlign = previousAlign
}

/**
 * Shrinks a text block until its wrapped height fits the space reserved for it.
 *
 * Measures for real rather than estimating, and steps down in 5% increments with
 * a floor — a claim that shrank without limit to fit a hostile box would be its
 * own kind of bug. Returns the block unchanged when it already fits, which is the
 * usual case.
 */
function fitBlock(ctx: PaintContext, text: string, block: TextBlock): TextBlock {
  let size = block.size
  const minimum = block.size * 0.35

  while (size > minimum) {
    ctx.font = `${WEIGHT_HERO} ${size}px ${FONT_DISPLAY}`
    const lineHeight = size * 1.05
    if (wrapLines(ctx, text, block.maxWidth).length * lineHeight <= block.height) break
    size *= 0.95
  }
  return { ...block, size, lineHeight: size * 1.05 }
}

/** Word-wraps within `block.maxWidth`, for the one block that can need two lines. */
export function drawWrapped(ctx: PaintContext, text: string, block: TextBlock): void {
  let y = block.y
  for (const each of wrapLines(ctx, text, block.maxWidth)) {
    drawTracked(ctx, each, { ...block, y })
    y += block.lineHeight
  }
}

/** Greedy word wrap against the current `ctx.font`. */
function wrapLines(ctx: PaintContext, text: string, maxWidth: number): string[] {
  const lines: string[] = []
  let line = ''

  for (const word of text.split(' ')) {
    const candidate = line === '' ? word : `${line} ${word}`
    if (line !== '' && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line)
      line = word
    } else {
      line = candidate
    }
  }
  if (line !== '') lines.push(line)
  return lines
}
